/**
 * A module to handle debugging.
 * @module Debug
 */
class Debug {

  /**
   * Creates an instance of the class.
   */
  constructor() {

    // Only enable if explicitly specified in the URL
    this.enabled = window.location.hash.includes("debug");

    // Track all messages in the order received
    this.messages = [];

    // Create the debug UI panel for displaying
    this.element = document.createElement("ul");
    this.element.setAttribute("id", "debug");

    // If enabled, append the debug element to the DOM and add class
    if (window.location.hash.includes("debug=true")) {
      document.body.appendChild(this.element);
      document.body.classList.add("debug");
    }

  }

  /**
   * Method to log the message.
   */
  log(message, data = null) {

    // If data was provided, format it for viewing
    if (data) {
      message += "\n" + JSON.stringify(data, null, 2);
    }

    // Keep track of every message
    this.messages.push(message);

    // Log the message to the console
    console.log(message);

    // Replace new lines with HTML line breaks
    message = message.replace(/(?:\r\n|\r|\n)/g, "<br>");

    // Add the message to the debug element list
    this.element.innerHTML += `<li>${message}</li>`;

  }

  /**
   * Method to return the mock user data for debug specific scenarios.
   */
  readUserMockData() {

    return {
      userId: "kp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxa",
      emailAddress: "user-a@example.com",
      firstName: "John",
      lastName: "Doe",
      mood: 12,
      location: { lat: 0, lng: 0 },
      createdAt: "2025-01-01T00:00:00.000Z",
      updatedAt: "2025-01-01T00:00:00.000Z",
      isSuspended: 0,
      note: "",
      subscriptions: ["xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"],
      reminders: {
        remindedAt: "2025-01-01 12:00:00",
        remindAt: "2025-01-02 12:30:00",
        push: { enabled: true },
        rcs: { enabled: false },
        email: { enabled: false },
      },
    };

  }

  /**
   * Method to return the mock family data for debug specific scenarios.
   */
  readFamiliesMockData(options = {}) {

    // Change the offsets for each user to test specific timing
    let userDateOffsetA = options.offsetA || 0;
    let userDateOffsetB = options.offsetB || 0;
    let userDateOffsetC = options.offsetC || 0;

    // Create the various dates to get the timestamps below
    let userDateA = new Date();
    userDateA.setHours(userDateA.getHours() + userDateOffsetA);
    let userDateB = new Date();
    userDateB.setHours(userDateB.getHours() + userDateOffsetB);
    let userDateC = new Date();
    userDateC.setHours(userDateC.getHours() + userDateOffsetC);

    // Return the mock data using the dates above
    return [
      {
        familyId: 1,
        createdAt: "2025-01-01T00:00:00.000Z",
        updatedAt: "2025-01-01T00:00:00.000Z",
        familyName: "Local Testing",
        members: [
          {
            emailAddress: "user-a@example.com",
            emailMasked: "u****a@example.com",
            userId: "kp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxa",
            familyId: 1,
            firstName: "John",
            lastName: "Doe",
            memberState: 1,
            mood: 11,
            location: "{\"lat\":0,\"lng\":0}",
            updatedAt: userDateA.toISOString(),
            createdAt: "2025-01-01T00:00:00.000Z",
          },
          {
            emailAddress: "user-b@example.com",
            emailMasked: "u****b@example.com",
            userId: "kp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxb",
            familyId: 1,
            firstName: "Alice",
            lastName: "Smith",
            memberState: 4,
            mood: 12,
            location: "{\"lat\":0,\"lng\":0}",
            updatedAt: userDateB.toISOString(),
            createdAt: "2025-01-01T00:00:00.000Z",
          },
          {
            emailAddress: "user-c@example.com",
            emailMasked: "u****c@example.com",
            userId: "kp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxc",
            familyId: 1,
            firstName: "Bob",
            lastName: "Smith",
            memberState: 4,
            mood: 11,
            location: "{\"lat\":0,\"lng\":0}",
            updatedAt: userDateC.toISOString(),
            createdAt: "2025-01-01T00:00:00.000Z",
          },
          {
            emailAddress: "user-z@example.com",
            emailMasked: "u****z@example.com",
            userId: null,
            familyId: 1,
            firstName: null,
            lastName: null,
            memberState: 8,
            mood: null,
            location: null,
            updatedAt: null,
            createdAt: null,
          },
        ],
      },
      {
        familyId: 2,
        createdAt: "2025-01-01T00:00:00.000Z",
        updatedAt: "2025-01-01T00:00:00.000Z",
        familyName: "Testing Longer Secondary",
        members: [
          {
            emailAddress: "user-a@example.com",
            emailMasked: "u****a@example.com",
            userId: "kp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxa",
            familyId: 2,
            firstName: "John",
            lastName: "Doe",
            memberState: 1,
            mood: 11,
            location: "{\"lat\":0,\"lng\":0}",
            updatedAt: userDateA.toISOString(),
            createdAt: "2025-01-01T00:00:00.000Z",
          },
          {
            emailAddress: "user-b@example.com",
            emailMasked: "u****b@example.com",
            userId: "kp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxb",
            familyId: 2,
            firstName: "Alice",
            lastName: "Smith",
            memberState: 4,
            mood: 12,
            location: "{\"lat\":0,\"lng\":0}",
            updatedAt: userDateB.toISOString(),
            createdAt: "2025-01-01T00:00:00.000Z",
          },
        ],
      },
    ];

  }

}

// Export the class as a singleton / static class by creating an instance
export default new Debug();
